/**
 * Decision Engine - Red Flags Management
 *
 * Manages red flag definitions for AI-powered claim risk detection.
 * v2 SafetyCulture format with severity levels and detection methods.
 */

import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { SectionHeader } from '@client/components/_ui/data-display/SectionHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { type Severity, SeverityTag } from '@client/components/_ui/display/tags';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Badge, Form, Input, InputNumber, Select, Switch, Tag } from 'antd';
import { Flag, Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

type RedFlagFormInput = {
    redFlagId: string;
    redFlagName: string;
    category: string;
    scope: string;
    summary: string | null;
    detectionMethod: string | null;
    severity: 'Low' | 'Medium' | 'High';
    suggestedAction: string | null;
    sortOrder: number;
    isActive: boolean;
};

const FormItem = Form.Item<RedFlagFormInput>;

/** The stored severity label mapped onto the typed severity scale; an unknown label renders no tag. */
const RED_FLAG_SEVERITY_VALUES: Partial<Record<string, Severity>> = {
    Low: 'low',
    Medium: 'medium',
    High: 'high',
} satisfies Record<RedFlagFormInput['severity'], Severity>;

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/$productTypeId/decision-engine/red-flags',
)({
    staticData: {
        metadata: {
            title: 'Red Flags',
        },
    },
    component: RedFlagsManagement,
});

function RedFlagsManagement() {
    const { productTypeId } = useParams({ strict: false });
    const { trpc, trpcUtils } = useTrpc();
    const [form] = Form.useForm<RedFlagFormInput>();

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState<bigint | null>(null);
    // The form mounts with the drawer, so the values it opens on are held here and passed as `initialValues`.
    const initialValuesRef = useRef<RedFlagFormInput | null>(null);
    const formDirty = useFormDirty();
    const closeRef = useRef<(() => void) | null>(null);
    const formId = 'red-flag-form';

    // Fetch red flags
    const { data, isLoading } = trpc.claim.decisionEngine.admin.listRedFlags.useQuery(
        { productTypeId: BigInt(productTypeId!) },
        { enabled: !!productTypeId },
    );
    const redFlags = data?.rows ?? [];

    // Mutations
    const { mutate: createRedFlag, isPending: isCreating } = trpc.claim.decisionEngine.admin.createRedFlag.useMutation({
        onSuccess() {
            message.success('Red flag created.');
            void trpcUtils.claim.decisionEngine.admin.listRedFlags.invalidate();
            void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
            closeRef.current?.();
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    const { mutate: updateRedFlag, isPending: isUpdating } = trpc.claim.decisionEngine.admin.updateRedFlag.useMutation({
        onSuccess() {
            message.success('Red flag updated.');
            void trpcUtils.claim.decisionEngine.admin.listRedFlags.invalidate();
            void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
            closeRef.current?.();
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    const { mutate: deleteRedFlag } = trpc.claim.decisionEngine.admin.deleteRedFlag.useMutation({
        onSuccess() {
            message.success('Red flag deleted.');
            void trpcUtils.claim.decisionEngine.admin.listRedFlags.invalidate();
            void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    // Table columns
    const columnHelper = createColumnHelper<(typeof redFlags)[number]>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('redFlagId', {
                id: 'redFlagId',
                header: 'ID',
                width: 120,
                cell: ({ row }) => <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.redFlagId}</code>,
            }),
            columnHelper.accessor('redFlagName', {
                id: 'redFlagName',
                header: 'Name',
                width: 220,
            }),
            columnHelper.accessor('category', {
                id: 'category',
                header: 'Category',
                width: 160,
                cell: ({ row }) => <Tag>{row.category}</Tag>,
            }),
            columnHelper.accessor('scope', {
                id: 'scope',
                header: 'Scope',
                width: 160,
            }),
            columnHelper.accessor('severity', {
                id: 'severity',
                header: 'Severity',
                width: 100,
                // Severity is stored as free text; rows written before the Low/Medium/High constraint
                // keep their raw value in a plain tag rather than disappearing.
                cell: ({ row }) => {
                    const severity = RED_FLAG_SEVERITY_VALUES[row.severity];
                    return severity ? <SeverityTag value={severity} /> : <Tag>{row.severity}</Tag>;
                },
            }),
            columnHelper.accessor('isActive', {
                id: 'isActive',
                header: 'Status',
                width: 100,
                cell: ({ row }) => (
                    <Badge status={row.isActive ? 'success' : 'default'} text={row.isActive ? 'Active' : 'Inactive'} />
                ),
            }),
            columnHelper.actions({
                id: 'actions',
                cell: ({ row }) => (
                    <Space size="small">
                        <Button.Edit onClick={() => openEditDrawer(row)} />
                        <Button.Delete
                            confirmTitle="Delete red flag"
                            confirmDescription="Are you sure you want to delete this red flag definition?"
                            onConfirm={(onSuccess) =>
                                deleteRedFlag(
                                    { id: row.id },
                                    {
                                        onSuccess: () => {
                                            onSuccess();
                                        },
                                        onError: (error) => {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                ),
                fixed: 'right',
            }),
        ],
        [columnHelper, deleteRedFlag],
    );

    const tableProps = usePrismaTable({
        data: redFlags,
        total: data?.total ?? 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        pagination: { position: 'none' },
        emptyState: { noRecordsTitle: 'No red flags yet' },
    });

    // Drawer handlers
    const openCreateDrawer = () => {
        setEditingId(null);
        initialValuesRef.current = {
            redFlagId: '',
            redFlagName: '',
            category: '',
            scope: '',
            summary: null,
            detectionMethod: null,
            severity: 'Medium',
            suggestedAction: null,
            sortOrder: 0,
            isActive: true,
        };
        setDrawerOpen(true);
    };

    const openEditDrawer = (redFlag: (typeof redFlags)[number]) => {
        setEditingId(redFlag.id);
        initialValuesRef.current = {
            redFlagId: redFlag.redFlagId,
            redFlagName: redFlag.redFlagName,
            category: redFlag.category,
            scope: redFlag.scope,
            summary: redFlag.summary,
            detectionMethod: redFlag.detectionMethod,
            severity: redFlag.severity as RedFlagFormInput['severity'],
            suggestedAction: redFlag.suggestedAction,
            sortOrder: redFlag.sortOrder,
            isActive: redFlag.isActive,
        };
        setDrawerOpen(true);
    };

    // Runs after the closing animation, whether the drawer was dismissed or closed after a save.
    const resetDrawer = () => {
        form.resetFields();
        formDirty.markClean();
        initialValuesRef.current = null;
        setDrawerOpen(false);
        setEditingId(null);
    };

    const onFinish = (values: RedFlagFormInput) => {
        const data = {
            redFlagId: values.redFlagId,
            redFlagName: values.redFlagName,
            category: values.category,
            scope: values.scope,
            summary: values.summary,
            detectionMethod: values.detectionMethod,
            severity: values.severity,
            suggestedAction: values.suggestedAction,
            sortOrder: values.sortOrder,
            isActive: values.isActive,
        };

        if (editingId) {
            updateRedFlag({ id: editingId, data });
        } else {
            createRedFlag({ productTypeId: BigInt(productTypeId!), data });
        }
    };

    return (
        <PageController route={Route}>
            <div className="mb-6 flex items-start gap-4 rounded-lg border border-warning-border bg-warning-subtle p-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-warning-subtle">
                    <Flag className="h-5 w-5 text-warning" />
                </div>
                <div>
                    <SectionHeader as="h3" size="small" title="Risk indicators" className="mb-1" />
                    <p className="text-muted-foreground text-sm">
                        Red flags help the AI identify claims requiring additional scrutiny. Each flag has a severity
                        level and detection method that drive risk assessment decisions.
                    </p>
                </div>
            </div>
            <TableToolbar
                actions={
                    <Button type="primary" icon={<Plus />} onClick={openCreateDrawer}>
                        Add red flag
                    </Button>
                }
            />
            <PrismaTable {...tableProps} />

            {drawerOpen && (
                <OverlayPage
                    title={editingId ? 'Edit red flag' : 'Add red flag'}
                    size="small"
                    isDirty={formDirty.isDirty}
                    afterClose={resetDrawer}
                    footer={({ requestClose }) => (
                        <DrawerFooter
                            onCancel={requestClose}
                            formId={formId}
                            okText={editingId ? 'Save changes' : 'Create'}
                            okLoading={isCreating || isUpdating}
                        />
                    )}
                >
                    {({ onClose }) => {
                        closeRef.current = onClose;
                        return (
                            <Form<RedFlagFormInput>
                                form={form}
                                {...formDirty.formProps}
                                layout="vertical"
                                onFinish={onFinish}
                                id={formId}
                                clearOnDestroy
                                initialValues={initialValuesRef.current ?? undefined}
                            >
                                <FormItem
                                    label="Red flag ID"
                                    name="redFlagId"
                                    rules={[{ required: true, message: 'Red flag ID is required' }]}
                                    tooltip="Unique identifier for this red flag"
                                >
                                    <Input placeholder="e.g., RF001" />
                                </FormItem>

                                <FormItem
                                    label="Name"
                                    name="redFlagName"
                                    rules={[{ required: true, message: 'Name is required' }]}
                                >
                                    <Input placeholder="e.g., Material inconsistency" />
                                </FormItem>

                                <FormItem
                                    label="Category"
                                    name="category"
                                    rules={[{ required: true, message: 'Category is required' }]}
                                >
                                    <Input placeholder="e.g., Narrative integrity" />
                                </FormItem>

                                <FormItem
                                    label="Scope"
                                    name="scope"
                                    rules={[{ required: true, message: 'Scope is required' }]}
                                >
                                    <Input placeholder="e.g., All claim types" />
                                </FormItem>

                                <FormItem label="Summary" name="summary">
                                    <Input.TextArea
                                        rows={3}
                                        placeholder="Brief description of what this red flag indicates"
                                    />
                                </FormItem>

                                <FormItem label="Detection method" name="detectionMethod">
                                    <Input.TextArea rows={3} placeholder="How the AI detects this red flag" />
                                </FormItem>

                                <FormItem
                                    label="Severity"
                                    name="severity"
                                    rules={[{ required: true, message: 'Severity is required' }]}
                                >
                                    <Select
                                        placeholder="Select severity"
                                        options={[
                                            { label: 'Low', value: 'Low' },
                                            { label: 'Medium', value: 'Medium' },
                                            { label: 'High', value: 'High' },
                                        ]}
                                    />
                                </FormItem>

                                <FormItem label="Suggested action" name="suggestedAction">
                                    <Input.TextArea
                                        rows={3}
                                        placeholder="Recommended action when this flag is raised"
                                    />
                                </FormItem>

                                <div className="mt-4 border-separator border-t pt-4">
                                    <FormItem
                                        label="Sort order"
                                        name="sortOrder"
                                        tooltip="Lower numbers appear first within category"
                                    >
                                        <InputNumber min={0} className="w-full" />
                                    </FormItem>

                                    <FormItem label="Active" name="isActive" valuePropName="checked">
                                        <Switch />
                                    </FormItem>
                                </div>
                            </Form>
                        );
                    }}
                </OverlayPage>
            )}
        </PageController>
    );
}
